type Difficulty = 'easy' | 'normal' | 'hard' | 'hell'
type LeaderboardBoard = 'index' | 'rally'

interface MatchPerformanceSnapshot {
  matchId: string
  difficulty: Difficulty
  p1: number
  p2: number
  isComplete: boolean
  maxRallyCount: number
  playerHits: number
  playerCenterHits: number
  playerReceiveAttempts: number
  playerReceiveSuccesses: number
  prePositionAttempts?: number
  prePositionSuccesses?: number
  highPressureAttempts?: number
  highPressureReturns?: number
  reactionSamples?: number
  avgReactionMs: number | null
  bestReactionMs: number | null
  reactionP50Ms?: number | null
  reactionP75Ms?: number | null
  reactionP90Ms?: number | null
  highestBallSpeed: number
  avgReturnSpeed?: number
  highSpeedReturnRate?: number
  returnSpeedSamples?: number
  returnQuality?: number
  returnQualitySamples?: number
  keyPointAttempts?: number
  keyPointWins?: number
  clutchSaveMs?: number | null
  curveShotCount?: number
  spinSamples?: number
  spinConversions?: number
  spinWeakReturns?: number
  maxSpin: number
  spinThreat: number
  chargeHits: number
  chargeThreats: number
  chargeConversions: number
  skillUses: number
  skillConversions: number
}

interface PingPongUiState {
  reason: string
  difficulty: Difficulty
  difficultyLabel: string
  canChangeDifficulty: boolean
  hasStarted: boolean
  gameActive: boolean
  gameOver: boolean
  rallyCount: number
  maxRallyCount: number
  score: {
    p1: number
    p2: number
  }
  performance?: MatchPerformanceSnapshot
}

interface LeaderboardEntry {
  id: string
  deviceId: string
  name: string
  score: number
  updatedAt: string
  rating?: string
  title?: string
  certDifficulty?: Difficulty
  recentAverage?: number
  maxRally?: number
  bestReactionMs?: number | null
}

interface LeaderboardResponse {
  entries: LeaderboardEntry[]
  limit: number
  total: number
  userRank: number | null
  userEntry: LeaderboardEntry | null
}

interface DisplayEntry extends LeaderboardEntry {
  rank: number
  isCurrent: boolean
  isLive: boolean
}

interface RatingTier {
  min: number
  max: number
  rating: string
  title: string
}

interface MatchSummary {
  matchId: string
  difficulty: Difficulty
  difficultyLabel: string
  p1: number
  p2: number
  won: boolean
  maxRallyCount: number
  score: number
  rating: string
  title: string
  certDifficulty: Difficulty
  avgReactionMs: number | null
  bestReactionMs: number | null
  receiveSuccessRate: number
  playerReceiveAttempts?: number
  playerReceiveSuccesses?: number
  sweetSpotRate: number
  playerHits?: number
  playerCenterHits?: number
  highestBallSpeed: number
  avgReturnSpeed?: number
  highSpeedReturnRate?: number
  returnSpeedSamples?: number
  returnQuality?: number
  returnQualitySamples?: number
  prePositionAttempts?: number
  prePositionSuccesses?: number
  prePositionRate?: number
  highPressureAttempts?: number
  highPressureReturns?: number
  highPressureReceiveRate?: number
  keyPointAttempts?: number
  keyPointWins?: number
  keyPointWinRate?: number
  spinThreat: number
  spinSamples?: number
  spinConversions?: number
  spinWeakReturns?: number
  maxSpin: number
  threatReactionP50Ms: number | null
  reactionP75Ms?: number | null
  reactionSamples?: number
  clutchSaveMs: number | null
  curveShotCount: number
  chargeEfficiency: number
  skillConversion: number
  pressureConversion?: number
  pressureConversionAttempts?: number
  pressureConversionCount?: number
  tempoPressure?: number
  createdAt: string
}

interface PersonalBest {
  maxRally: number
  bestReactionMs: number | null
  hellMaxRally: number
  bestThreatReactionP50Ms: number | null
  bestPrePositionRate: number
  bestPrePositionAttempts: number
  bestPrePositionSuccesses: number
  bestClutchSaveMs: number | null
  bestCurveShotCount: number
  highestBallSpeed: number
  maxSpin: number
  bestMatchScore: number
  bestMatchCertDifficulty: Difficulty
  bestIndex: number
  bestRating: string
  bestTitle: string
  bestCertDifficulty: Difficulty
}

type CoachState = 'prestart' | 'playing' | 'finished'

interface CoachMetricInsight {
  id: string
  label: string
  value: string
  score: number
  trusted: boolean
  goal: string
  advice: string
}

interface BoardState {
  entries: LeaderboardEntry[]
  available: boolean
}

const DIFFICULTIES: Array<{ level: Difficulty; label: string; hint: string }> = [
  { level: 'easy', label: '容易', hint: '最高B' },
  { level: 'normal', label: '中等', hint: '最高A+' },
  { level: 'hard', label: '困难', hint: '最高SS' },
  { level: 'hell', label: '地狱', hint: '最高SSS+' },
]

const RATING_TIERS: RatingTier[] = [
  { min: 0, max: 199, rating: 'C', title: '入门' },
  { min: 200, max: 349, rating: 'B', title: '熟练' },
  { min: 350, max: 499, rating: 'A', title: '强者' },
  { min: 500, max: 649, rating: 'A+', title: '精英' },
  { min: 650, max: 779, rating: 'S', title: '大师' },
  { min: 780, max: 879, rating: 'S+', title: '宗师' },
  { min: 880, max: 939, rating: 'SS', title: '王者' },
  { min: 940, max: 979, rating: 'SS+', title: '巅峰' },
  { min: 980, max: 999, rating: 'SSS+', title: '传奇' },
]

const DIFFICULTY_LABELS: Record<Difficulty, string> = {
  easy: '容易',
  normal: '中等',
  hard: '困难',
  hell: '地狱',
}

const DIFFICULTY_RANK: Record<Difficulty, number> = {
  easy: 1,
  normal: 2,
  hard: 3,
  hell: 4,
}

const HUMAN_REACTION_REFERENCE_MU_MS = 230
const HUMAN_REACTION_REFERENCE_SIGMA_MS = 30
const HUMAN_REACTION_REFERENCE_TAU_MS = 54
const HUMAN_REACTION_REFERENCE_MIN_MS = 120
const HUMAN_REACTION_REFERENCE_MAX_MS = 1200

const DIFFICULTY_SCORE_CAP: Record<Difficulty, number> = {
  easy: 349,
  normal: 649,
  hard: 939,
  hell: 999,
}

const DIFFICULTY_SCORE_MULTIPLIER: Record<Difficulty, number> = {
  easy: 0.7,
  normal: 1,
  hard: 1.2,
  hell: 1.4,
}

const MATCH_SCORE_WEIGHTS = {
  rally: 0.22,
  receiveStability: 0.2,
  result: 0.34,
  reaction: 0.14,
  prePosition: 0.1,
} as const

const DEVICE_ID_KEY = 'pingPong.hellLeaderboard.deviceId'
const DEVICE_SECRET_KEY = 'pingPong.hellLeaderboard.deviceSecret'
const PLAYER_NAME_KEY = 'pingPong.hellLeaderboard.name'
const RECENT_MATCHES_KEY = 'pingPong.competitive.recentMatches.v6'
const PERSONAL_BEST_KEY = 'pingPong.competitive.personalBest.v6'
const RALLY_API_URL = '/api/leaderboard/hell-rally'
const INDEX_API_URL = '/api/leaderboard/competitive-index'
const REGISTER_API_URL = '/api/device/register'
const MATCH_START_API_URL = '/api/match/start'
const MATCH_HEARTBEAT_API_URL = '/api/match/heartbeat'
const MATCH_FINISH_API_URL = '/api/match/finish'
const MAX_NAME_LENGTH = 20
const LEADERBOARD_LIMIT = 50
const LIVE_SUBMIT_INTERVAL_MS = 1200
const RECENT_MATCH_LIMIT = 5
const RECEIVE_TRUSTED_SAMPLE_COUNT = 5
const PREPOSITION_TRUSTED_SAMPLE_COUNT = 5
const HIGH_PRESSURE_TRUSTED_SAMPLE_COUNT = 3
const RETURN_QUALITY_TRUSTED_SAMPLE_COUNT = 5
const CONTROL_TRUSTED_SAMPLE_COUNT = 5
const TEMPO_TRUSTED_SAMPLE_COUNT = 5
const KEY_POINT_TRUSTED_SAMPLE_COUNT = 3
const USE_STATIC_MOBILE_LEADERBOARDS = true

const STATIC_MOBILE_LEADERBOARDS: Record<LeaderboardBoard, LeaderboardEntry[]> = {
  index: [
    { id: 'seed-index-xianyu', deviceId: 'seed-index-xianyu', name: '咸鱼', score: 872, rating: 'S+', title: '宗师', certDifficulty: 'hell', recentAverage: 872, maxRally: 16, bestReactionMs: 318, updatedAt: '2026-05-15T00:01:00.000Z' },
    { id: 'seed-index-kongkong', deviceId: 'seed-index-kongkong', name: 'KongKong', score: 846, rating: 'S+', title: '宗师', certDifficulty: 'hell', recentAverage: 846, maxRally: 17, bestReactionMs: 326, updatedAt: '2026-05-15T00:02:00.000Z' },
    { id: 'seed-index-coffee', deviceId: 'seed-index-coffee', name: '咖啡', score: 678, rating: 'S', title: '大师', certDifficulty: 'hard', recentAverage: 678, maxRally: 15, bestReactionMs: 354, updatedAt: '2026-05-15T00:03:00.000Z' },
    { id: 'seed-index-tt', deviceId: 'seed-index-tt', name: 'TT', score: 642, rating: 'A+', title: '精英', certDifficulty: 'hard', recentAverage: 642, maxRally: 14, bestReactionMs: 368, updatedAt: '2026-05-15T00:04:00.000Z' },
    { id: 'seed-index-xing', deviceId: 'seed-index-xing', name: 'xing', score: 588, rating: 'A+', title: '精英', certDifficulty: 'hard', recentAverage: 588, maxRally: 11, bestReactionMs: 382, updatedAt: '2026-05-15T00:05:00.000Z' },
    { id: 'seed-index-hahahaer', deviceId: 'seed-index-hahahaer', name: '哈哈哈儿', score: 521, rating: 'A+', title: '精英', certDifficulty: 'normal', recentAverage: 521, maxRally: 13, bestReactionMs: 391, updatedAt: '2026-05-15T00:06:00.000Z' },
    { id: 'seed-index-kimi', deviceId: 'seed-index-kimi', name: 'kimi', score: 466, rating: 'A', title: '强者', certDifficulty: 'normal', recentAverage: 466, maxRally: 9, bestReactionMs: 406, updatedAt: '2026-05-15T00:07:00.000Z' },
  ],
  rally: [
    { id: 'seed-rally-kongkong', deviceId: 'seed-rally-kongkong', name: 'KongKong', score: 17, certDifficulty: 'hell', updatedAt: '2026-05-15T00:11:00.000Z' },
    { id: 'seed-rally-xianyu', deviceId: 'seed-rally-xianyu', name: '咸鱼', score: 16, certDifficulty: 'hell', updatedAt: '2026-05-15T00:12:00.000Z' },
    { id: 'seed-rally-coffee', deviceId: 'seed-rally-coffee', name: '咖啡', score: 15, certDifficulty: 'hell', updatedAt: '2026-05-15T00:13:00.000Z' },
    { id: 'seed-rally-tt', deviceId: 'seed-rally-tt', name: 'TT', score: 14, certDifficulty: 'hell', updatedAt: '2026-05-15T00:14:00.000Z' },
    { id: 'seed-rally-hahahaer', deviceId: 'seed-rally-hahahaer', name: '哈哈哈儿', score: 13, certDifficulty: 'hell', updatedAt: '2026-05-15T00:15:00.000Z' },
    { id: 'seed-rally-xing', deviceId: 'seed-rally-xing', name: 'xing', score: 11, certDifficulty: 'hell', updatedAt: '2026-05-15T00:16:00.000Z' },
    { id: 'seed-rally-kimi', deviceId: 'seed-rally-kimi', name: 'kimi', score: 9, certDifficulty: 'hell', updatedAt: '2026-05-15T00:17:00.000Z' },
  ],
}

const EMPTY_PERFORMANCE: MatchPerformanceSnapshot = {
  matchId: 'boot',
  difficulty: 'normal',
  p1: 0,
  p2: 0,
  isComplete: false,
  maxRallyCount: 0,
  playerHits: 0,
  playerCenterHits: 0,
  playerReceiveAttempts: 0,
  playerReceiveSuccesses: 0,
  prePositionAttempts: 0,
  prePositionSuccesses: 0,
  highPressureAttempts: 0,
  highPressureReturns: 0,
  reactionSamples: 0,
  avgReactionMs: null,
  bestReactionMs: null,
  reactionP50Ms: null,
  reactionP75Ms: null,
  reactionP90Ms: null,
  highestBallSpeed: 0,
  avgReturnSpeed: 0,
  highSpeedReturnRate: 0,
  returnSpeedSamples: 0,
  returnQuality: 0,
  returnQualitySamples: 0,
  keyPointAttempts: 0,
  keyPointWins: 0,
  clutchSaveMs: null,
  curveShotCount: 0,
  spinSamples: 0,
  spinConversions: 0,
  spinWeakReturns: 0,
  maxSpin: 0,
  spinThreat: 0,
  chargeHits: 0,
  chargeThreats: 0,
  chargeConversions: 0,
  skillUses: 0,
  skillConversions: 0,
}

const DEFAULT_STATE: PingPongUiState = {
  reason: 'boot',
  difficulty: 'normal',
  difficultyLabel: '中等',
  canChangeDifficulty: true,
  hasStarted: false,
  gameActive: false,
  gameOver: false,
  rallyCount: 0,
  maxRallyCount: 0,
  score: { p1: 0, p2: 0 },
  performance: EMPTY_PERFORMANCE,
}

interface MatchSession {
  clientMatchId: string
  serverMatchId: string
  matchToken: string
  expiresAt: number
  startedAt: number
  difficulty: Difficulty
}

let initialized = false
let state: PingPongUiState = DEFAULT_STATE
let boards: Record<LeaderboardBoard, BoardState> = {
  index: { entries: STATIC_MOBILE_LEADERBOARDS.index, available: true },
  rally: { entries: STATIC_MOBILE_LEADERBOARDS.rally, available: true },
}
let activeBoard: LeaderboardBoard = 'index'
let selectedPerformanceView: 'current' | 'recent' | null = null
let playerName = readStoredName()
let deviceId = ''
let deviceSecret = ''
let currentSession: MatchSession | null = null
let pendingMatchFinish: MatchPerformanceSnapshot | null = null
let editingName = false
let pendingSubmitScore: number | null = null
let previousGameOver = false
let savedMatchIds = new Set<string>()
let recentMatches = readRecentMatches()
let personalBest = readPersonalBest()
let lastLiveSubmitAt = 0
let lastLiveSubmittedScore = 0
let queuedLiveSubmitScore = 0
let liveSubmitTimer: number | null = null
let liveSubmitInFlight = false

function byId<T extends HTMLElement>(id: string): T | null {
  return document.getElementById(id) as T | null
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}

function pct(numerator: number, denominator: number): number {
  if (denominator <= 0) return 0
  return clamp((numerator / denominator) * 100, 0, 100)
}

function sumBy<T>(items: T[], getter: (item: T) => number | undefined): number {
  return items.reduce((sum, item) => sum + Math.max(0, getter(item) ?? 0), 0)
}

function medianRounded(values: number[]): number | null {
  const sorted = values
    .filter((value) => Number.isFinite(value))
    .sort((a, b) => a - b)
  if (sorted.length <= 0) return null
  const middle = Math.floor(sorted.length / 2)
  if (sorted.length % 2 === 1) return Math.round(sorted[middle])
  return Math.round((sorted[middle - 1] + sorted[middle]) / 2)
}

function erf(value: number): number {
  const sign = value < 0 ? -1 : 1
  const x = Math.abs(value)
  const a1 = 0.254829592
  const a2 = -0.284496736
  const a3 = 1.421413741
  const a4 = -1.453152027
  const a5 = 1.061405429
  const t = 1 / (1 + 0.3275911 * x)
  const y = 1 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x)
  return sign * y
}

function normalCdf(value: number): number {
  return 0.5 * (1 + erf(value / Math.SQRT2))
}

function getHumanReactionReferenceCdf(reactionMs: number): number {
  const z = (reactionMs - HUMAN_REACTION_REFERENCE_MU_MS) / HUMAN_REACTION_REFERENCE_SIGMA_MS
  const v = z - HUMAN_REACTION_REFERENCE_SIGMA_MS / HUMAN_REACTION_REFERENCE_TAU_MS
  const exponent = -(reactionMs - HUMAN_REACTION_REFERENCE_MU_MS) / HUMAN_REACTION_REFERENCE_TAU_MS +
    (HUMAN_REACTION_REFERENCE_SIGMA_MS ** 2) / (2 * HUMAN_REACTION_REFERENCE_TAU_MS ** 2)
  return clamp(normalCdf(z) - Math.exp(exponent) * normalCdf(v), 0, 1)
}

function getHumanReactionFasterThanPercent(avgReactionMs: number | null): number | null {
  if (avgReactionMs === null || !Number.isFinite(avgReactionMs)) return null
  const reactionMs = clamp(avgReactionMs, HUMAN_REACTION_REFERENCE_MIN_MS, HUMAN_REACTION_REFERENCE_MAX_MS)
  return Math.round(clamp((1 - getHumanReactionReferenceCdf(reactionMs)) * 100, 0, 99))
}

function formatReactionBenchmarkLabel(avgReactionMs: number | null): string {
  const fasterThan = getHumanReactionFasterThanPercent(avgReactionMs)
  if (fasterThan === null) return '样本不足'
  if (fasterThan <= 0) return '超过1%玩家'
  return `超过${fasterThan}%玩家`
}

function truncateName(value: string): string {
  return Array.from(value.trim()).slice(0, MAX_NAME_LENGTH).join('')
}

function readStoredName(): string {
  try {
    return truncateName(localStorage.getItem(PLAYER_NAME_KEY) ?? localStorage.getItem('pingPong.mobile.playerName') ?? 'Jin')
  } catch {
    return 'Jin'
  }
}

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return fallback
    return JSON.parse(raw) as T
  } catch {
    return fallback
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // Private browsing can block storage; gameplay should continue.
  }
}

function getMatchStatKey(match: Partial<MatchSummary>): string {
  return [
    match.score ?? 0,
    match.p1 ?? 0,
    match.p2 ?? 0,
    match.maxRallyCount ?? 0,
    match.threatReactionP50Ms ?? match.avgReactionMs ?? '',
    match.playerReceiveAttempts ?? 0,
    match.playerReceiveSuccesses ?? 0,
    match.prePositionAttempts ?? 0,
    match.prePositionSuccesses ?? 0,
    match.highPressureAttempts ?? 0,
    match.highPressureReturns ?? 0,
  ].join('|')
}

function dedupeRecentMatches(matches: MatchSummary[]): MatchSummary[] {
  const seen = new Set<string>()
  const unique: MatchSummary[] = []
  for (const match of matches) {
    const key = getMatchStatKey(match)
    if (seen.has(key)) continue
    seen.add(key)
    unique.push(match)
  }
  return unique.slice(0, RECENT_MATCH_LIMIT)
}

function readRecentMatches(): MatchSummary[] {
  const raw = readJson<MatchSummary[]>(RECENT_MATCHES_KEY, [])
  return Array.isArray(raw) ? dedupeRecentMatches(raw) : []
}

function createEmptyPersonalBest(): PersonalBest {
  return {
    maxRally: 0,
    bestReactionMs: null,
    hellMaxRally: 0,
    bestThreatReactionP50Ms: null,
    bestPrePositionRate: 0,
    bestPrePositionAttempts: 0,
    bestPrePositionSuccesses: 0,
    bestClutchSaveMs: null,
    bestCurveShotCount: 0,
    highestBallSpeed: 0,
    maxSpin: 0,
    bestMatchScore: 0,
    bestMatchCertDifficulty: 'easy',
    bestIndex: 0,
    bestRating: 'C',
    bestTitle: '入门',
    bestCertDifficulty: 'easy',
  }
}

function readPersonalBest(): PersonalBest {
  const stored = readJson<Partial<PersonalBest>>(PERSONAL_BEST_KEY, {})
  const migratedBestMatchScore = typeof stored.bestMatchScore === 'number'
    ? stored.bestMatchScore
    : recentMatches.length > 0 ? 0 : (stored.bestIndex ?? 0)
  return {
    ...createEmptyPersonalBest(),
    ...stored,
    bestMatchScore: migratedBestMatchScore,
    bestMatchCertDifficulty: stored.bestMatchCertDifficulty ?? stored.bestCertDifficulty ?? 'easy',
    bestIndex: migratedBestMatchScore,
  }
}

function getDeviceId(): string {
  try {
    const stored = localStorage.getItem(DEVICE_ID_KEY)
    if (stored) return stored

    const cryptoWithUuid = crypto as Crypto & { randomUUID?: () => string }
    const next = cryptoWithUuid.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`
    localStorage.setItem(DEVICE_ID_KEY, next)
    return next
  } catch {
    return `session-${Date.now()}-${Math.random().toString(36).slice(2)}`
  }
}

function rotateDeviceId(): string {
  try {
    localStorage.removeItem(DEVICE_ID_KEY)
    localStorage.removeItem(DEVICE_SECRET_KEY)
  } catch {
    // localStorage 在隐私模式下可能被禁用，可忽略
  }
  deviceSecret = ''
  return getDeviceId()
}

function readStoredDeviceSecret(): string {
  try {
    return localStorage.getItem(DEVICE_SECRET_KEY) ?? ''
  } catch {
    return ''
  }
}

function writeStoredDeviceSecret(value: string): void {
  try {
    if (value) localStorage.setItem(DEVICE_SECRET_KEY, value)
    else localStorage.removeItem(DEVICE_SECRET_KEY)
  } catch {
    // ignore
  }
}

async function ensureDeviceCredentials(): Promise<boolean> {
  if (deviceId && deviceSecret) return true

  for (let attempt = 0; attempt < 3; attempt += 1) {
    if (!deviceId) deviceId = getDeviceId()
    if (deviceSecret) return true

    try {
      const response = await fetch(REGISTER_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deviceId }),
      })
      if (response.ok) {
        const data = (await response.json()) as { deviceSecret?: string }
        if (data.deviceSecret) {
          deviceSecret = data.deviceSecret
          writeStoredDeviceSecret(deviceSecret)
          return true
        }
        return false
      }
      if (response.status === 409) {
        // 服务端已占用此 deviceId 但本地丢失了 secret，换一个新 deviceId 重试
        deviceId = rotateDeviceId()
        continue
      }
      return false
    } catch {
      return false
    }
  }
  return false
}

async function ensureMatchSession(difficulty: Difficulty, clientMatchId: string): Promise<MatchSession | null> {
  if (
    currentSession
    && currentSession.clientMatchId === clientMatchId
    && currentSession.difficulty === difficulty
    && currentSession.expiresAt > Date.now() + 5_000
  ) {
    return currentSession
  }

  currentSession = null

  const credsOk = await ensureDeviceCredentials()
  if (!credsOk) return null

  try {
    const response = await fetch(MATCH_START_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ deviceId, deviceSecret, difficulty }),
    })
    if (!response.ok) {
      if (response.status === 401) {
        deviceSecret = ''
        writeStoredDeviceSecret('')
      }
      return null
    }
    const data = (await response.json()) as {
      matchId: string
      matchToken: string
      startedAt: string
      expiresAt: string
    }
    const session: MatchSession = {
      clientMatchId,
      serverMatchId: data.matchId,
      matchToken: data.matchToken,
      expiresAt: Date.parse(data.expiresAt),
      startedAt: Date.parse(data.startedAt) || Date.now(),
      difficulty,
    }
    currentSession = session
    return session
  } catch {
    return null
  }
}

function buildFinishStats(performance: MatchPerformanceSnapshot) {
  return {
    p1: performance.p1,
    p2: performance.p2,
    maxRallyCount: performance.maxRallyCount,
    playerHits: performance.playerHits,
    playerCenterHits: performance.playerCenterHits,
    playerReceiveAttempts: performance.playerReceiveAttempts,
    playerReceiveSuccesses: performance.playerReceiveSuccesses,
    prePositionAttempts: performance.prePositionAttempts ?? 0,
    prePositionSuccesses: performance.prePositionSuccesses ?? 0,
    highPressureAttempts: performance.highPressureAttempts ?? 0,
    highPressureReturns: performance.highPressureReturns ?? 0,
    reactionSamples: performance.reactionSamples ?? 0,
    avgReactionMs: performance.avgReactionMs,
    bestReactionMs: performance.bestReactionMs,
    reactionP50Ms: performance.reactionP50Ms ?? null,
    reactionP75Ms: performance.reactionP75Ms ?? null,
    reactionP90Ms: performance.reactionP90Ms ?? null,
    highestBallSpeed: performance.highestBallSpeed,
    avgReturnSpeed: performance.avgReturnSpeed ?? 0,
    highSpeedReturnRate: performance.highSpeedReturnRate ?? 0,
    returnSpeedSamples: performance.returnSpeedSamples ?? 0,
    returnQuality: performance.returnQuality ?? 0,
    returnQualitySamples: performance.returnQualitySamples ?? 0,
    keyPointAttempts: performance.keyPointAttempts ?? 0,
    keyPointWins: performance.keyPointWins ?? 0,
    clutchSaveMs: performance.clutchSaveMs ?? null,
    curveShotCount: performance.curveShotCount ?? 0,
    spinSamples: performance.spinSamples ?? 0,
    spinConversions: performance.spinConversions ?? 0,
    spinWeakReturns: performance.spinWeakReturns ?? 0,
    maxSpin: performance.maxSpin,
    spinThreat: performance.spinThreat,
    chargeHits: performance.chargeHits,
    chargeThreats: performance.chargeThreats,
    chargeConversions: performance.chargeConversions,
    skillUses: performance.skillUses,
    skillConversions: performance.skillConversions,
  }
}

function getRating(score: number): RatingTier {
  const safeScore = clamp(Math.round(score), 0, 999)
  return RATING_TIERS.find((tier) => safeScore >= tier.min && safeScore <= tier.max) ?? RATING_TIERS[0]
}

function getNextRating(score: number): RatingTier | null {
  const safeScore = clamp(Math.round(score), 0, 999)
  return RATING_TIERS.find((tier) => safeScore < tier.min) ?? null
}

function getNextRatingProgressText(score: number): string {
  const next = getNextRating(score)
  if (!next) return '已达最高评级'
  return `距 ${next.rating} ${next.title}还差 ${Math.max(0, next.min - Math.round(score))}`
}

function getRatingProgress(score: number): number {
  const safeScore = clamp(Math.round(score), 0, 999)
  const tier = getRating(safeScore)
  const next = getNextRating(safeScore)
  if (!next) return 100
  return clamp(((safeScore - tier.min) / Math.max(1, next.min - tier.min)) * 100, 0, 100)
}

function getDifficultyLabel(difficulty: Difficulty): string {
  return DIFFICULTY_LABELS[difficulty]
}

function getCertificationLabel(difficulty: Difficulty): string {
  if (difficulty === 'hell') return '地狱难度'
  if (difficulty === 'hard') return '困难难度'
  if (difficulty === 'normal') return '中等难度'
  return '容易难度'
}

function normalizeEntry(entry: Partial<LeaderboardEntry>): LeaderboardEntry | null {
  const score = Math.max(0, Math.floor(Number(entry.score) || 0))
  const entryDeviceId = String(entry.deviceId ?? '').trim()
  if (!entryDeviceId || score <= 0) return null

  const ratingTier = getRating(score)
  const certDifficulty = normalizeDifficulty(entry.certDifficulty)

  return {
    id: String(entry.id ?? entryDeviceId),
    deviceId: entryDeviceId,
    name: truncateName(String(entry.name ?? 'Player')) || 'Player',
    score,
    rating: String(entry.rating ?? ratingTier.rating),
    title: String(entry.title ?? ratingTier.title),
    certDifficulty,
    recentAverage: Math.round(Number(entry.recentAverage) || score),
    maxRally: Math.max(0, Math.floor(Number(entry.maxRally) || 0)),
    bestReactionMs: entry.bestReactionMs === null || entry.bestReactionMs === undefined
      ? null
      : Math.max(0, Math.floor(Number(entry.bestReactionMs) || 0)),
    updatedAt: String(entry.updatedAt ?? new Date().toISOString()),
  }
}

function syncPersonalBestFromIndexEntry(entry: LeaderboardEntry | null): void {
  if (!entry || entry.deviceId !== deviceId || entry.score <= 0) return

  const certDifficulty = normalizeDifficulty(entry.certDifficulty)
  const shouldUseEntry = entry.score > personalBest.bestMatchScore ||
    (
      entry.score === personalBest.bestMatchScore &&
      DIFFICULTY_RANK[certDifficulty] > DIFFICULTY_RANK[personalBest.bestMatchCertDifficulty]
    )

  if (!shouldUseEntry) return

  const ratingTier = getRating(entry.score)
  personalBest = {
    ...personalBest,
    maxRally: Math.max(personalBest.maxRally, entry.maxRally ?? 0),
    hellMaxRally: certDifficulty === 'hell'
      ? Math.max(personalBest.hellMaxRally, entry.maxRally ?? 0)
      : personalBest.hellMaxRally,
    bestMatchScore: entry.score,
    bestMatchCertDifficulty: certDifficulty,
    bestIndex: entry.score,
    bestRating: entry.rating ?? ratingTier.rating,
    bestTitle: entry.title ?? ratingTier.title,
    bestCertDifficulty: DIFFICULTY_RANK[certDifficulty] >= DIFFICULTY_RANK[personalBest.bestCertDifficulty]
      ? certDifficulty
      : personalBest.bestCertDifficulty,
  }
  writeJson(PERSONAL_BEST_KEY, personalBest)
}

function normalizeDifficulty(value: unknown): Difficulty {
  if (value === 'easy' || value === 'normal' || value === 'hard' || value === 'hell') return value
  return 'easy'
}

function sortEntries<T extends LeaderboardEntry>(items: T[], board: LeaderboardBoard): T[] {
  return [...items].sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score
    if (board === 'index') {
      const diffRank = DIFFICULTY_RANK[b.certDifficulty ?? 'easy'] - DIFFICULTY_RANK[a.certDifficulty ?? 'easy']
      if (diffRank !== 0) return diffRank
      if ((b.maxRally ?? 0) !== (a.maxRally ?? 0)) return (b.maxRally ?? 0) - (a.maxRally ?? 0)
      const aReaction = a.bestReactionMs ?? Number.POSITIVE_INFINITY
      const bReaction = b.bestReactionMs ?? Number.POSITIVE_INFINITY
      if (aReaction !== bReaction) return aReaction - bReaction
    }
    return Date.parse(a.updatedAt) - Date.parse(b.updatedAt)
  })
}

function applyLeaderboardResponse(board: LeaderboardBoard, data: Partial<LeaderboardResponse>): void {
  boards[board].entries = Array.isArray(data.entries)
    ? data.entries.map(normalizeEntry).filter((entry): entry is LeaderboardEntry => Boolean(entry))
    : []
  boards[board].available = true

  if (board === 'index') {
    const userEntry = data.userEntry ? normalizeEntry(data.userEntry) : null
    syncPersonalBestFromIndexEntry(userEntry ?? boards.index.entries.find((entry) => entry.deviceId === deviceId) ?? null)
  }
}

async function refreshLeaderboard(board: LeaderboardBoard): Promise<void> {
  if (USE_STATIC_MOBILE_LEADERBOARDS) {
    boards[board].available = true
    return
  }

  const url = board === 'index' ? INDEX_API_URL : RALLY_API_URL
  try {
    const response = await fetch(`${url}?deviceId=${encodeURIComponent(deviceId)}`, { cache: 'no-store' })
    if (!response.ok) throw new Error(`Leaderboard ${response.status}`)
    applyLeaderboardResponse(board, await response.json())
  } catch {
    boards[board].available = false
  }
}

async function refreshLeaderboards(): Promise<void> {
  await Promise.all([refreshLeaderboard('index'), refreshLeaderboard('rally')])
  renderAll()
}

async function submitRallyScore(score: number): Promise<boolean> {
  const safeScore = Math.max(0, Math.floor(score))
  if (safeScore <= 0) return false

  if (!playerName) {
    pendingSubmitScore = Math.max(pendingSubmitScore ?? 0, safeScore)
    editingName = true
    renderNameArea()
    renderLeaderboard()
    return false
  }

  // 仅 hell 难度才会写入 rally 榜（服务端会再次校验）
  if (state.difficulty !== 'hell') return false

  const clientMatchId = state.performance?.matchId ?? `live-${deviceId}`
  const session = await ensureMatchSession('hell', clientMatchId)
  if (!session) {
    pendingSubmitScore = safeScore
    boards.rally.available = false
    renderLeaderboard()
    return false
  }

  try {
    const response = await fetch(MATCH_HEARTBEAT_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        matchId: session.serverMatchId,
        matchToken: session.matchToken,
        deviceId,
        name: playerName,
        currentMaxRally: safeScore,
      }),
    })
    if (!response.ok) {
      if (response.status === 401 || response.status === 409 || response.status === 410) {
        currentSession = null
      }
      throw new Error(`heartbeat ${response.status}`)
    }
    applyLeaderboardResponse('rally', await response.json())
    pendingSubmitScore = null
    renderLeaderboard()
    return true
  } catch {
    pendingSubmitScore = safeScore
    boards.rally.available = false
    renderLeaderboard()
    return false
  }
}

async function submitMatchFinish(performance: MatchPerformanceSnapshot): Promise<boolean> {
  if (!performance.isComplete) return false

  if (!playerName) {
    pendingMatchFinish = performance
    editingName = true
    renderNameArea()
    renderLeaderboard()
    return false
  }

  const session = await ensureMatchSession(performance.difficulty, performance.matchId)
  if (!session) {
    pendingMatchFinish = performance
    boards.index.available = false
    renderLeaderboard()
    return false
  }

  const durationSeconds = Math.max(3, Math.floor((Date.now() - session.startedAt) / 1000))
  const currentSummary = computeMatchScore(performance)
  const bestMatch = getBestMatchRecord()
  const bestPayload = bestMatch.score > 0 && bestMatch.score !== currentSummary.score
    ? { score: bestMatch.score, certDifficulty: bestMatch.certDifficulty }
    : null
  const recent = [
    ...recentMatches
      .filter((match) => match.matchId !== performance.matchId)
      .map((match) => ({
        score: match.score,
        certDifficulty: match.certDifficulty,
      })),
  ].slice(0, RECENT_MATCH_LIMIT - 1)

  try {
    const response = await fetch(MATCH_FINISH_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        matchId: session.serverMatchId,
        matchToken: session.matchToken,
        deviceId,
        name: playerName,
        durationSeconds,
        stats: buildFinishStats(performance),
        bestMatch: bestPayload,
        recentMatches: recent,
      }),
    })
    if (!response.ok) {
      if (response.status === 401 || response.status === 409 || response.status === 410) {
        currentSession = null
      }
      throw new Error(`finish ${response.status}`)
    }
    const data = (await response.json()) as {
      leaderboards: { rally: Partial<LeaderboardResponse>; index: Partial<LeaderboardResponse> }
    }
    if (data.leaderboards?.rally) applyLeaderboardResponse('rally', data.leaderboards.rally)
    if (data.leaderboards?.index) applyLeaderboardResponse('index', data.leaderboards.index)
    pendingMatchFinish = null
    currentSession = null
    renderAll()
    return true
  } catch {
    pendingMatchFinish = performance
    boards.index.available = false
    renderLeaderboard()
    return false
  }
}

function queueLiveScoreSubmit(score: number, force = false): void {
  if (USE_STATIC_MOBILE_LEADERBOARDS) return

  const safeScore = Math.max(0, Math.floor(score))
  if (safeScore <= 0) return

  if (!force && safeScore <= lastLiveSubmittedScore) return

  queuedLiveSubmitScore = Math.max(queuedLiveSubmitScore, safeScore)

  if (force) {
    void flushLiveScoreSubmit()
    return
  }

  if (liveSubmitTimer) return

  const elapsed = Date.now() - lastLiveSubmitAt
  const delay = Math.max(0, LIVE_SUBMIT_INTERVAL_MS - elapsed)
  liveSubmitTimer = window.setTimeout(() => {
    liveSubmitTimer = null
    void flushLiveScoreSubmit()
  }, delay)
}

async function flushLiveScoreSubmit(): Promise<void> {
  if (liveSubmitTimer) {
    window.clearTimeout(liveSubmitTimer)
    liveSubmitTimer = null
  }

  if (liveSubmitInFlight) return

  const score = queuedLiveSubmitScore
  if (score <= 0) return

  queuedLiveSubmitScore = 0
  liveSubmitInFlight = true
  lastLiveSubmitAt = Date.now()

  const submitted = await submitRallyScore(score)
  if (submitted) {
    lastLiveSubmittedScore = Math.max(lastLiveSubmittedScore, score)
  } else if (playerName && boards.rally.available) {
    queuedLiveSubmitScore = Math.max(queuedLiveSubmitScore, score)
  }

  liveSubmitInFlight = false

  if (queuedLiveSubmitScore > lastLiveSubmittedScore) {
    queueLiveScoreSubmit(queuedLiveSubmitScore)
  }
}

function getLiveIndexSummary(): MatchSummary | null {
  const performance = state.performance ?? EMPTY_PERFORMANCE
  if (state.gameOver || performance.isComplete) return null
  if (!hasEnoughLiveScoreSamples(performance)) return null

  const summary = computeMatchScore(performance)
  return summary.score > 0 ? summary : null
}

function buildDisplayEntries(board: LeaderboardBoard = activeBoard): DisplayEntry[] {
  const displayEntries = boards[board].entries.map((entry) => ({
    ...entry,
    isCurrent: entry.deviceId === deviceId,
    isLive: false,
  }))

  if (board === 'rally' && state.maxRallyCount > 0) {
    const existingIndex = displayEntries.findIndex((entry) => entry.deviceId === deviceId)
    const existing = existingIndex >= 0 ? displayEntries.splice(existingIndex, 1)[0] : null
    const liveScore = Math.max(state.maxRallyCount, existing?.score ?? 0)

    displayEntries.push({
      id: `live-${deviceId}`,
      deviceId,
      name: playerName || '你',
      score: liveScore,
      updatedAt: existing?.updatedAt ?? new Date().toISOString(),
      isCurrent: true,
      isLive: state.gameActive && liveScore > (existing?.score ?? 0),
    })
  }

  if (board === 'index') {
    const bestMatch = getBestMatchRecord()
    const liveSummary = getLiveIndexSummary()
    const existingIndex = displayEntries.findIndex((entry) => entry.deviceId === deviceId)
    const existing = existingIndex >= 0 ? displayEntries[existingIndex] : null
    const currentBestScore = Math.max(existing?.score ?? 0, bestMatch.score)
    const shouldUseLiveSummary = Boolean(liveSummary && liveSummary.score >= currentBestScore)
    const shouldUseBestMatch = !shouldUseLiveSummary && bestMatch.score > (existing?.score ?? 0)

    if (shouldUseLiveSummary || shouldUseBestMatch) {
      if (existingIndex >= 0) displayEntries.splice(existingIndex, 1)
      const sourceScore = shouldUseLiveSummary ? liveSummary!.score : bestMatch.score
      const ratingTier = getRating(sourceScore)
      displayEntries.push({
        id: `live-index-${deviceId}`,
        deviceId,
        name: playerName || existing?.name || '你',
        score: sourceScore,
        rating: (shouldUseLiveSummary ? liveSummary!.rating : bestMatch.rating) || ratingTier.rating,
        title: (shouldUseLiveSummary ? liveSummary!.title : bestMatch.title) || ratingTier.title,
        certDifficulty: shouldUseLiveSummary ? liveSummary!.certDifficulty : bestMatch.certDifficulty,
        recentAverage: shouldUseLiveSummary ? liveSummary!.score : getRecentAverage(),
        maxRally: shouldUseLiveSummary ? liveSummary!.maxRallyCount : personalBest.maxRally,
        bestReactionMs: shouldUseLiveSummary
          ? liveSummary!.threatReactionP50Ms ?? liveSummary!.bestReactionMs
          : personalBest.bestThreatReactionP50Ms ?? personalBest.bestReactionMs,
        updatedAt: shouldUseLiveSummary ? liveSummary!.createdAt : existing?.updatedAt ?? new Date().toISOString(),
        isCurrent: true,
        isLive: shouldUseLiveSummary,
      })
    }
  }

  return sortEntries(displayEntries, board).map((entry, index) => ({
    ...entry,
    rank: index + 1,
  }))
}

function reactionScore(avgReactionMs: number | null): number {
  return getHumanReactionFasterThanPercent(avgReactionMs) ?? 0
}

function getPointShare(performance: MatchPerformanceSnapshot): number {
  const total = performance.p1 + performance.p2
  if (total <= 0) return 0
  return performance.p1 / total
}

function getResultScore(performance: MatchPerformanceSnapshot): number {
  const total = performance.p1 + performance.p2
  if (total <= 0) return 0

  const pointShare = getPointShare(performance) * 100
  if (performance.p1 > performance.p2) {
    return clamp(55 + pointShare * 0.45, 0, 100)
  }

  return clamp(pointShare * 0.85, 0, 65)
}

function getEffectiveReceiveStability(performance: MatchPerformanceSnapshot, rawReceiveRate: number): number {
  const pointShare = getPointShare(performance) * 100
  const rallyControl = clamp((performance.maxRallyCount / 18) * 100, 0, 100)

  return clamp(rawReceiveRate * 0.45 + pointShare * 0.35 + rallyControl * 0.2, 0, 100)
}

function getRallyContextCap(performance: MatchPerformanceSnapshot): number {
  const rally = performance.maxRallyCount
  const won = performance.p1 > performance.p2

  if (rally <= 0) return 0
  if (rally < 4) return won ? 349 : 99
  if (rally < 6) return won ? 549 : 199
  if (rally < 10) return won ? 699 : 249
  if (rally < 16) return won ? 779 : 399
  if (rally < 24) return won ? 879 : 649
  if (rally < 36) return 939
  return 999
}

function getSampleSizeCap(performance: MatchPerformanceSnapshot): number {
  const pointCount = performance.p1 + performance.p2
  const touchSamples = performance.playerHits + performance.playerReceiveAttempts

  if (performance.isComplete) {
    const won = performance.p1 > performance.p2
    if (performance.maxRallyCount < 4) return won ? 349 : 199
    if (performance.maxRallyCount < 6) return won ? 549 : 199
    if (performance.maxRallyCount < 10 && pointCount >= 10 && performance.p1 < performance.p2) return 249
    return 999
  }

  if (pointCount < 5 || touchSamples < 10) return 199
  if (pointCount < 8 || touchSamples < 16) return 249
  if (pointCount < 11 || touchSamples < 22) return 349
  return 999
}

function getScorelineCap(performance: MatchPerformanceSnapshot): number {
  const total = performance.p1 + performance.p2
  if (total <= 0) return 999
  if (performance.p1 >= performance.p2) return 999

  const deficit = performance.p2 - performance.p1
  if (deficit >= 8) return 199
  if (deficit >= 5) return performance.maxRallyCount >= 16 ? 349 : 299
  if (deficit >= 3) return performance.maxRallyCount >= 18 ? 399 : 349
  if (performance.maxRallyCount >= 16) return 779
  return 649
}

function computeMatchScore(performance: MatchPerformanceSnapshot): MatchSummary {
  const receiveSuccessRate = pct(performance.playerReceiveSuccesses, performance.playerReceiveAttempts)
  const effectiveReceiveStability = getEffectiveReceiveStability(performance, receiveSuccessRate)
  const sweetSpotRate = pct(performance.playerCenterHits, performance.playerHits)
  const threatReactionP50Ms = performance.reactionP50Ms ?? performance.avgReactionMs
  const prePositionRate = pct(performance.prePositionSuccesses ?? 0, performance.prePositionAttempts ?? 0)
  const highPressureReceiveRate = pct(performance.highPressureReturns ?? 0, performance.highPressureAttempts ?? 0)
  const hasTrustedPrePosition = (performance.prePositionAttempts ?? 0) >= PREPOSITION_TRUSTED_SAMPLE_COUNT
  const hasTrustedReturnQuality = (performance.returnQualitySamples ?? 0) >= RETURN_QUALITY_TRUSTED_SAMPLE_COUNT
  const hasTrustedKeyPoint = (performance.keyPointAttempts ?? 0) >= KEY_POINT_TRUSTED_SAMPLE_COUNT
  const trustedPrePositionRate = hasTrustedPrePosition ? prePositionRate : 0
  const returnQuality = hasTrustedReturnQuality ? clamp(performance.returnQuality ?? 0, 0, 100) : 0
  const keyPointWinRate = hasTrustedKeyPoint ? pct(performance.keyPointWins ?? 0, performance.keyPointAttempts ?? 0) : 0
  const chargeEfficiency = performance.chargeThreats > 0
    ? pct(performance.chargeConversions, performance.chargeThreats)
    : performance.chargeHits > 0 ? 42 : 0
  const skillConversion = performance.skillUses > 0 ? pct(performance.skillConversions, performance.skillUses) : 0
  const pressureConversion = getPressureConversion(performance)
  const rallyScore = clamp((performance.maxRallyCount / 24) * 100, 0, 100)
  const resultScore = getResultScore(performance)
  const hasSamples = performance.maxRallyCount > 0
    || performance.playerHits > 0
    || performance.playerReceiveAttempts > 0
    || performance.highestBallSpeed > 0
  const baseScore = hasSamples
    ? rallyScore * MATCH_SCORE_WEIGHTS.rally +
      effectiveReceiveStability * MATCH_SCORE_WEIGHTS.receiveStability +
      resultScore * MATCH_SCORE_WEIGHTS.result +
      reactionScore(threatReactionP50Ms) * MATCH_SCORE_WEIGHTS.reaction +
      trustedPrePositionRate * MATCH_SCORE_WEIGHTS.prePosition
    : 0
  const difficulty = performance.difficulty
  const cappedScore = Math.min(
    DIFFICULTY_SCORE_CAP[difficulty],
    getRallyContextCap(performance),
    getScorelineCap(performance),
    getSampleSizeCap(performance),
    Math.round(baseScore * 10 * DIFFICULTY_SCORE_MULTIPLIER[difficulty]),
  )
  const ratingTier = getRating(cappedScore)

  return {
    matchId: performance.matchId,
    difficulty,
    difficultyLabel: getDifficultyLabel(difficulty),
    p1: performance.p1,
    p2: performance.p2,
    won: performance.p1 > performance.p2,
    maxRallyCount: performance.maxRallyCount,
    score: cappedScore,
    rating: ratingTier.rating,
    title: ratingTier.title,
    certDifficulty: difficulty,
    avgReactionMs: performance.avgReactionMs,
    bestReactionMs: performance.bestReactionMs,
    receiveSuccessRate: Math.round(receiveSuccessRate),
    playerReceiveAttempts: performance.playerReceiveAttempts,
    playerReceiveSuccesses: performance.playerReceiveSuccesses,
    sweetSpotRate: Math.round(sweetSpotRate),
    playerHits: performance.playerHits,
    playerCenterHits: performance.playerCenterHits,
    highestBallSpeed: Math.round(performance.highestBallSpeed),
    avgReturnSpeed: Math.round(performance.avgReturnSpeed ?? 0),
    highSpeedReturnRate: Math.round(performance.highSpeedReturnRate ?? 0),
    returnSpeedSamples: Math.max(0, Math.floor(performance.returnSpeedSamples ?? 0)),
    returnQuality: Math.round(returnQuality),
    returnQualitySamples: Math.max(0, Math.floor(performance.returnQualitySamples ?? 0)),
    prePositionAttempts: Math.max(0, Math.floor(performance.prePositionAttempts ?? 0)),
    prePositionSuccesses: Math.max(0, Math.floor(performance.prePositionSuccesses ?? 0)),
    prePositionRate: Math.round(prePositionRate),
    highPressureAttempts: Math.max(0, Math.floor(performance.highPressureAttempts ?? 0)),
    highPressureReturns: Math.max(0, Math.floor(performance.highPressureReturns ?? 0)),
    highPressureReceiveRate: Math.round(highPressureReceiveRate),
    keyPointAttempts: Math.max(0, Math.floor(performance.keyPointAttempts ?? 0)),
    keyPointWins: Math.max(0, Math.floor(performance.keyPointWins ?? 0)),
    keyPointWinRate: Math.round(keyPointWinRate),
    spinThreat: Math.round(clamp(performance.spinThreat, 0, 100)),
    spinSamples: Math.max(0, Math.floor(performance.spinSamples ?? 0)),
    spinConversions: Math.max(0, Math.floor(performance.spinConversions ?? 0)),
    spinWeakReturns: Math.max(0, Math.floor(performance.spinWeakReturns ?? 0)),
    maxSpin: Math.round(performance.maxSpin),
    threatReactionP50Ms,
    reactionP75Ms: performance.reactionP75Ms ?? null,
    reactionSamples: Math.max(0, Math.floor(performance.reactionSamples ?? 0)),
    clutchSaveMs: performance.clutchSaveMs ?? null,
    curveShotCount: Math.max(0, Math.floor(performance.curveShotCount ?? 0)),
    chargeEfficiency: Math.round(chargeEfficiency),
    skillConversion: Math.round(skillConversion),
    pressureConversion: pressureConversion.score,
    pressureConversionAttempts: pressureConversion.attempts,
    pressureConversionCount: pressureConversion.conversions,
    tempoPressure: getTempoPressureScore(performance),
    createdAt: new Date().toISOString(),
  }
}

function computeCompetitiveIndex(matches: Array<{ score: number }> = [getBestMatchRecord()]): number {
  if (matches.length === 0) return 0
  return Math.max(...matches.map((match) => match.score), 0)
}

function getRecentAverage(matches = recentMatches): number {
  if (matches.length === 0) return 0
  return Math.round(matches.reduce((sum, match) => sum + match.score, 0) / matches.length)
}

function getRecentTrendLabel(matches = recentMatches): string {
  if (matches.length < 2) return matches.length > 0 ? '样本积累中' : '--'
  const current = getRecentAverage(matches)
  const previousMatches = matches.slice(1)
  const previous = getRecentAverage(previousMatches)
  if (current > previous + 8) return '上升'
  if (current < previous - 8) return '下滑'
  return '持平'
}

function computeRecentCoachSummary(matches = recentMatches): MatchSummary | null {
  const recent = matches.slice(0, RECENT_MATCH_LIMIT)
  if (recent.length <= 0) return null

  const score = computeCompetitiveIndex(recent)
  const ratingTier = getRating(score)
  const reactionValues = recent
    .map((match) => match.threatReactionP50Ms ?? match.avgReactionMs)
    .filter((value): value is number => value !== null && Number.isFinite(value))
  const reactionSamples = sumBy(recent, (match) => match.reactionSamples ?? (match.threatReactionP50Ms === null ? 0 : 1))
  const playerReceiveAttempts = sumBy(recent, (match) => match.playerReceiveAttempts)
  const playerReceiveSuccesses = sumBy(recent, (match) => match.playerReceiveSuccesses)
  const playerHits = sumBy(recent, (match) => match.playerHits)
  const playerCenterHits = sumBy(recent, (match) => match.playerCenterHits)
  const prePositionAttempts = sumBy(recent, (match) => match.prePositionAttempts)
  const prePositionSuccesses = sumBy(recent, (match) => match.prePositionSuccesses)
  const highPressureAttempts = sumBy(recent, (match) => match.highPressureAttempts)
  const highPressureReturns = sumBy(recent, (match) => match.highPressureReturns)
  const maxRallyCount = Math.max(...recent.map((match) => match.maxRallyCount), 0)
  const base = recent[0]

  return {
    ...base,
    matchId: 'recent-coach-summary',
    score,
    rating: ratingTier.rating,
    title: ratingTier.title,
    maxRallyCount,
    avgReactionMs: medianRounded(reactionValues),
    bestReactionMs: reactionValues.length > 0 ? Math.min(...reactionValues) : null,
    threatReactionP50Ms: medianRounded(reactionValues),
    reactionSamples,
    playerReceiveAttempts,
    playerReceiveSuccesses,
    receiveSuccessRate: pct(playerReceiveSuccesses, playerReceiveAttempts),
    playerHits,
    playerCenterHits,
    sweetSpotRate: pct(playerCenterHits, playerHits),
    prePositionAttempts,
    prePositionSuccesses,
    prePositionRate: pct(prePositionSuccesses, prePositionAttempts),
    highPressureAttempts,
    highPressureReturns,
    highPressureReceiveRate: pct(highPressureReturns, highPressureAttempts),
    createdAt: new Date().toISOString(),
  }
}

function getBestMatchRecord(): {
  score: number
  rating: string
  title: string
  certDifficulty: Difficulty
} {
  const storedBest = {
    score: personalBest.bestMatchScore,
    rating: personalBest.bestRating,
    title: personalBest.bestTitle,
    certDifficulty: personalBest.bestMatchCertDifficulty,
  }

  return recentMatches.reduce((best, match) => {
    if (match.score < best.score) return best
    return {
      score: match.score,
      rating: match.rating,
      title: match.title,
      certDifficulty: match.certDifficulty,
    }
  }, storedBest)
}

function getBestPrePositionRecord(): {
  rate: number
  attempts: number
  successes: number
} {
  const storedBest = {
    rate: personalBest.bestPrePositionRate,
    attempts: personalBest.bestPrePositionAttempts,
    successes: personalBest.bestPrePositionSuccesses,
  }

  return recentMatches.reduce((best, match) => {
    const attempts = match.prePositionAttempts ?? 0
    if (attempts < PREPOSITION_TRUSTED_SAMPLE_COUNT) return best

    const rate = match.prePositionRate ?? 0
    if (rate < best.rate) return best
    if (rate === best.rate && attempts <= best.attempts) return best

    return {
      rate,
      attempts,
      successes: match.prePositionSuccesses ?? Math.round((rate / 100) * attempts),
    }
  }, storedBest)
}

function updatePersonalBest(match: MatchSummary): void {
  const isBestMatch = match.score >= personalBest.bestMatchScore
  const nextBestMatchScore = Math.max(personalBest.bestMatchScore, match.score)
  const hasTrustedPrePosition = (match.prePositionAttempts ?? 0) >= PREPOSITION_TRUSTED_SAMPLE_COUNT
  const isBestPrePosition = hasTrustedPrePosition && (
    (match.prePositionRate ?? 0) > personalBest.bestPrePositionRate ||
    ((match.prePositionRate ?? 0) === personalBest.bestPrePositionRate &&
      (match.prePositionAttempts ?? 0) > personalBest.bestPrePositionAttempts)
  )
  personalBest = {
    maxRally: Math.max(personalBest.maxRally, match.maxRallyCount),
    bestReactionMs: match.bestReactionMs === null
      ? personalBest.bestReactionMs
      : personalBest.bestReactionMs === null
        ? match.bestReactionMs
        : Math.min(personalBest.bestReactionMs, match.bestReactionMs),
    hellMaxRally: match.difficulty === 'hell'
      ? Math.max(personalBest.hellMaxRally, match.maxRallyCount)
      : personalBest.hellMaxRally,
    bestThreatReactionP50Ms: match.threatReactionP50Ms === null
      ? personalBest.bestThreatReactionP50Ms
      : personalBest.bestThreatReactionP50Ms === null
        ? match.threatReactionP50Ms
        : Math.min(personalBest.bestThreatReactionP50Ms, match.threatReactionP50Ms),
    bestPrePositionRate: isBestPrePosition ? match.prePositionRate ?? 0 : personalBest.bestPrePositionRate,
    bestPrePositionAttempts: isBestPrePosition ? match.prePositionAttempts ?? 0 : personalBest.bestPrePositionAttempts,
    bestPrePositionSuccesses: isBestPrePosition ? match.prePositionSuccesses ?? 0 : personalBest.bestPrePositionSuccesses,
    bestClutchSaveMs: match.clutchSaveMs === null
      ? personalBest.bestClutchSaveMs
      : personalBest.bestClutchSaveMs === null
        ? match.clutchSaveMs
        : Math.min(personalBest.bestClutchSaveMs, match.clutchSaveMs),
    bestCurveShotCount: Math.max(personalBest.bestCurveShotCount, match.curveShotCount),
    highestBallSpeed: Math.max(personalBest.highestBallSpeed, match.highestBallSpeed),
    maxSpin: Math.max(personalBest.maxSpin, match.maxSpin),
    bestMatchScore: nextBestMatchScore,
    bestMatchCertDifficulty: isBestMatch ? match.certDifficulty : personalBest.bestMatchCertDifficulty,
    bestIndex: nextBestMatchScore,
    bestRating: isBestMatch ? match.rating : personalBest.bestRating,
    bestTitle: isBestMatch ? match.title : personalBest.bestTitle,
    bestCertDifficulty: DIFFICULTY_RANK[match.certDifficulty] >= DIFFICULTY_RANK[personalBest.bestCertDifficulty]
      ? match.certDifficulty
      : personalBest.bestCertDifficulty,
  }
  writeJson(PERSONAL_BEST_KEY, personalBest)
}

function saveCompletedMatch(performance: MatchPerformanceSnapshot): void {
  if (savedMatchIds.has(performance.matchId)) return
  savedMatchIds.add(performance.matchId)

  const summary = computeMatchScore(performance)
  recentMatches = dedupeRecentMatches([summary, ...recentMatches.filter((match) => match.matchId !== summary.matchId)])
  writeJson(RECENT_MATCHES_KEY, recentMatches)

  updatePersonalBest(summary)

  if (!USE_STATIC_MOBILE_LEADERBOARDS) {
    // score 由服务端再次基于上报的 stats 重新计算并写入榜单，
    // 客户端这里只负责本地 UI 缓存与触发提交流程。
    void submitMatchFinish(performance)
  }
}

function renderDifficulty(): void {
  const buttons = document.querySelectorAll<HTMLButtonElement>('[data-difficulty-option]')
  const statusEl = byId('difficulty-status')

  buttons.forEach((button) => {
    const level = button.dataset.difficultyOption
    const isActive = level === state.difficulty
    button.classList.toggle('is-active', isActive)
    button.disabled = !state.canChangeDifficulty
    button.setAttribute('aria-pressed', isActive ? 'true' : 'false')
  })

  if (!statusEl) return
  if (state.canChangeDifficulty) {
    statusEl.textContent = state.hasStarted ? '结算后可调整' : '开局前可调整'
    statusEl.dataset.locked = 'false'
  } else {
    statusEl.textContent = '对局中锁定'
    statusEl.dataset.locked = 'true'
  }
}

function renderNameArea(): void {
  const form = byId<HTMLFormElement>('leaderboard-name-form')
  const input = byId<HTMLInputElement>('leaderboard-name-input')
  const saved = byId('leaderboard-saved-name')
  const savedName = byId('leaderboard-saved-name-value')
  if (!form || !input || !saved || !savedName) return

  form.hidden = Boolean(playerName) && !editingName
  saved.hidden = !playerName || editingName
  input.value = editingName ? playerName : ''
  savedName.textContent = playerName

  if (!playerName && pendingMatchFinish) {
    input.placeholder = '输入名称，保存本局综合指数'
  } else if (pendingSubmitScore && !playerName) {
    input.placeholder = `输入名称，保存 ${pendingSubmitScore} 连击`
  } else {
    input.placeholder = '输入上榜名称'
  }
}

function setText(id: string, value: string): void {
  const el = byId(id)
  if (el) el.textContent = value
}

function setMetricCard(id: string, value: string, detail: string, fill: number): void {
  setText(`metric-${id}`, value)
  setText(`metric-${id}-detail`, detail)
  const card = byId(`metric-${id}-card`)
  if (card) {
    card.style.setProperty('--metric-fill', `${Math.round(clamp(fill, 0, 100))}%`)
    card.classList.toggle('has-value', value !== '--' && value !== '采样中')
  }
}

function getTempoPressureScore(performance: MatchPerformanceSnapshot): number {
  const avgSpeed = performance.avgReturnSpeed ?? 0
  const avgSpeedScore = clamp(((avgSpeed - 850) / 1200) * 100, 0, 100)
  const highSpeedScore = clamp(performance.highSpeedReturnRate ?? 0, 0, 100)
  const rallyScore = clamp((performance.maxRallyCount / 18) * 100, 0, 100)
  return Math.round(avgSpeedScore * 0.48 + highSpeedScore * 0.28 + rallyScore * 0.24)
}

function getPressureConversion(performance: MatchPerformanceSnapshot): { score: number; attempts: number; conversions: number } {
  const spinConversions = (performance.spinConversions ?? 0) + (performance.spinWeakReturns ?? 0)
  const attempts = performance.chargeThreats + performance.skillUses + (performance.spinSamples ?? 0)
  const conversions = performance.chargeConversions + performance.skillConversions + spinConversions

  return {
    score: attempts > 0 ? Math.round(pct(conversions, attempts)) : 0,
    attempts,
    conversions,
  }
}

function hasPerformanceSamples(performance: MatchPerformanceSnapshot): boolean {
  return performance.maxRallyCount > 0
    || performance.playerHits > 0
    || performance.playerReceiveAttempts > 0
    || performance.highestBallSpeed > 0
}

function hasEnoughLiveScoreSamples(performance: MatchPerformanceSnapshot): boolean {
  if (performance.isComplete) return hasPerformanceSamples(performance)
  const pointCount = performance.p1 + performance.p2
  const touchSamples = performance.playerHits + performance.playerReceiveAttempts
  return pointCount >= 5 && touchSamples >= 10 && performance.maxRallyCount >= 6
}

function getResolvedPerformanceView(hasCurrentSamples: boolean, hasRecentMatch: boolean): 'current' | 'recent' {
  if (selectedPerformanceView === 'current' && hasCurrentSamples) return 'current'
  if (selectedPerformanceView === 'recent' && hasRecentMatch) return 'recent'
  return hasCurrentSamples || !hasRecentMatch ? 'current' : 'recent'
}

function syncPerformanceViewTabs(view: 'current' | 'recent', hasCurrentSamples: boolean, hasRecentMatch: boolean): void {
  document.querySelector<HTMLElement>('.ability-view-tabs')?.classList.toggle(
    'has-multiple-tabs',
    hasCurrentSamples && hasRecentMatch
  )
  document.querySelectorAll<HTMLButtonElement>('[data-performance-view]').forEach((button) => {
    const option = button.dataset.performanceView
    const available = option === 'current' ? hasCurrentSamples : hasRecentMatch
    button.disabled = !available
    button.hidden = !available
    button.classList.toggle('is-active', option === view)
    button.setAttribute('aria-pressed', option === view ? 'true' : 'false')
  })
}

function scrollTargetIntoPanel(
  scroller: HTMLElement | null,
  target: HTMLElement | null,
  block: 'start' | 'center' = 'start'
): void {
  if (!scroller || !target) return

  const scrollerRect = scroller.getBoundingClientRect()
  const targetRect = target.getBoundingClientRect()
  const targetTop = targetRect.top - scrollerRect.top + scroller.scrollTop
  const offset = block === 'center'
    ? (scroller.clientHeight - targetRect.height) / 2
    : 8

  scroller.scrollTo({
    top: Math.max(0, targetTop - offset),
    behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
  })
}

function focusGameOverPanelContent(): void {
  const leftScroller = document.querySelector<HTMLElement>('#ping-pong-side-panel .side-panel-scroll')
  const abilitySection = document.querySelector<HTMLElement>('#ping-pong-side-panel .ability-section:not(.is-empty)')
  scrollTargetIntoPanel(leftScroller, abilitySection, 'start')

  const rightScroller = document.querySelector<HTMLElement>('#ping-pong-leaderboard-panel .side-panel-scroll')
  const currentPodium = document.querySelector<HTMLElement>('#leaderboard-podium .is-current')
  const currentRow = document.querySelector<HTMLElement>('#leaderboard-list .is-current')
  if (currentPodium) {
    scrollTargetIntoPanel(rightScroller, currentPodium, 'center')
    return
  }

  scrollTargetIntoPanel(rightScroller, currentRow, 'center')
}

function scheduleGameOverPanelFocus(): void {
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(focusGameOverPanelContent)
  })
  window.setTimeout(focusGameOverPanelContent, 360)
}

function getMetricRateDetail(successes = 0, attempts = 0, suffix: string): string {
  return attempts > 0 ? `${successes}/${attempts} ${suffix}` : ''
}

function setRateMetric(
  id: string,
  rate: number | undefined,
  successes: number | undefined,
  attempts: number | undefined,
  suffix: string,
  trusted = true,
  emptyValue = '--'
): void {
  const safeRate = Math.round(clamp(rate ?? 0, 0, 100))
  const safeAttempts = Math.max(0, Math.floor(attempts ?? 0))
  const safeSuccesses = Math.max(0, Math.floor(successes ?? 0))
  const canShow = trusted && safeAttempts > 0
  setMetricCard(
    id,
    canShow ? `${safeRate}%` : emptyValue,
    canShow ? getMetricRateDetail(safeSuccesses, safeAttempts, suffix) : '',
    canShow ? safeRate : 0
  )
}

function getCoachState(): CoachState {
  if (state.gameOver) return 'finished'
  if (state.hasStarted) return 'playing'
  return 'prestart'
}

function getDisplayPlayerName(): string {
  return playerName || '你'
}

function setMetricWeakState(weakId: string | null): void {
  document.querySelectorAll<HTMLElement>('.ability-stat').forEach((card) => {
    card.classList.remove('is-weak')
  })
  if (!weakId) return
  byId(`metric-${weakId}-card`)?.classList.add('is-weak')
}

function createCoachMetrics(summary: MatchSummary): CoachMetricInsight[] {
  const prePositionRate = Math.round(clamp(summary.prePositionRate ?? 0, 0, 100))
  const defenseRate = Math.round(clamp(summary.receiveSuccessRate, 0, 100))
  const pressureRate = Math.round(clamp(summary.highPressureReceiveRate ?? 0, 0, 100))
  const controlRate = Math.round(clamp(summary.sweetSpotRate, 0, 100))
  const reactionMs = summary.threatReactionP50Ms
  const reactionTrusted = reactionMs !== null && (summary.reactionSamples ?? 0) > 0
  const rallyValue = Math.max(0, Math.floor(summary.maxRallyCount))

  return [
    {
      id: 'rally',
      label: '最高连击',
      value: rallyValue > 0 ? String(rallyValue) : '--',
      score: clamp((rallyValue / 20) * 100, 0, 100),
      trusted: rallyValue > 0,
      goal: `最高连击达到 ${Math.max(3, rallyValue + 2)}`,
      advice: '先稳住前两拍，把回球落点压回中路。',
    },
    {
      id: 'reaction',
      label: '反应速度',
      value: reactionTrusted ? `${reactionMs ?? 0}ms` : '--',
      score: reactionTrusted ? reactionScore(reactionMs) : 0,
      trusted: reactionTrusted,
      goal: reactionTrusted ? `反应速度提升到 ${Math.max(220, Math.round((reactionMs ?? 0) - 35))}ms` : '先完成更多来球样本',
      advice: '来球过中线前就启动，不要等球贴近板子才追。',
    },
    {
      id: 'pressure',
      label: '高压接球',
      value: (summary.highPressureAttempts ?? 0) >= HIGH_PRESSURE_TRUSTED_SAMPLE_COUNT ? `${pressureRate}%` : '--',
      score: pressureRate,
      trusted: (summary.highPressureAttempts ?? 0) >= HIGH_PRESSURE_TRUSTED_SAMPLE_COUNT,
      goal: `高压接球提升到 ${Math.min(80, Math.max(20, pressureRate + 12))}%`,
      advice: '高速球优先保证碰到中心区，别急着追求角度。',
    },
    {
      id: 'defense',
      label: '接球稳定',
      value: (summary.playerReceiveAttempts ?? 0) >= RECEIVE_TRUSTED_SAMPLE_COUNT ? `${defenseRate}%` : '--',
      score: defenseRate,
      trusted: (summary.playerReceiveAttempts ?? 0) >= RECEIVE_TRUSTED_SAMPLE_COUNT,
      goal: `接球稳定提升到 ${Math.min(82, Math.max(25, defenseRate + 10))}%`,
      advice: '先把板子移动到球路上，再用接触位置制造角度。',
    },
    {
      id: 'preposition',
      label: '预判站位',
      value: (summary.prePositionAttempts ?? 0) >= PREPOSITION_TRUSTED_SAMPLE_COUNT ? `${prePositionRate}%` : '--',
      score: prePositionRate,
      trusted: (summary.prePositionAttempts ?? 0) >= PREPOSITION_TRUSTED_SAMPLE_COUNT,
      goal: `预判站位提升到 ${Math.min(65, Math.max(15, prePositionRate + 15))}%`,
      advice: '球刚反弹时先贴近预测落点，减少最后一刻大幅追球。',
    },
    {
      id: 'control',
      label: '控球精度',
      value: (summary.playerHits ?? 0) >= CONTROL_TRUSTED_SAMPLE_COUNT ? `${controlRate}%` : '--',
      score: controlRate,
      trusted: (summary.playerHits ?? 0) >= CONTROL_TRUSTED_SAMPLE_COUNT,
      goal: `中心命中提升到 ${Math.min(70, Math.max(20, controlRate + 12))}%`,
      advice: '把滑动速度留到击球瞬间，减少板边擦球。',
    },
  ]
}

function pickCoachMetrics(summary: MatchSummary): {
  strongest: CoachMetricInsight | null
  weakness: CoachMetricInsight | null
} {
  const metrics = createCoachMetrics(summary)
  const trusted = metrics.filter((metric) => metric.trusted)
  if (trusted.length <= 0) {
    return { strongest: null, weakness: null }
  }

  const strongest = [...trusted].sort((a, b) => b.score - a.score)[0] ?? null
  const weaknessPriority = ['preposition', 'defense', 'pressure', 'reaction', 'control', 'rally']
  const weakness = trusted
    .filter((metric) => weaknessPriority.includes(metric.id))
    .sort((a, b) => {
      if (a.score !== b.score) return a.score - b.score
      return weaknessPriority.indexOf(a.id) - weaknessPriority.indexOf(b.id)
    })[0] ?? null

  return { strongest, weakness }
}

function renderCoachShell(coachState: CoachState): void {
  const panel = byId('ping-pong-side-panel')
  if (panel) {
    panel.dataset.coachState = coachState
    panel.dataset.hasHistory = recentMatches.length > 0 || personalBest.bestMatchScore > 0 ? 'true' : 'false'
  }
  setText('coach-status-difficulty', getDifficultyLabel(state.difficulty))
}

function renderCoachSummary(summary: MatchSummary, hasSummary: boolean, scope: 'current' | 'recent' = 'current'): void {
  const isRecentScope = scope === 'recent'
  document.querySelector<HTMLElement>('.coach-summary-section')?.setAttribute('data-summary-scope', scope)
  setText('coach-reaction-title', isRecentScope ? '近5局反应' : '反应时间')
  setText('coach-strong-title', isRecentScope ? '近5局最强' : '最强项')
  setText('coach-weak-title', isRecentScope ? '近5局短板' : '短板')

  if (!hasSummary) {
    setMetricWeakState(null)
    setText('coach-reaction-value', '--')
    setText('coach-reaction-label', '样本不足')
    setText('coach-strong-value', '--')
    setText('coach-strong-label', '完成一局后生成')
    setText('coach-weak-value', '--')
    setText('coach-weak-label', '暂无样本')
    return
  }

  const { strongest, weakness } = pickCoachMetrics(summary)
  setMetricWeakState(weakness?.id ?? null)
  const reactionMs = summary.threatReactionP50Ms
  if (reactionMs !== null && (summary.reactionSamples ?? 0) > 0) {
    setText('coach-reaction-value', `${reactionMs}ms`)
    setText('coach-reaction-label', formatReactionBenchmarkLabel(reactionMs))
  } else if (scope === 'recent' && summary.maxRallyCount > 0) {
    setText('coach-reaction-title', '近5局连击')
    setText('coach-reaction-value', String(summary.maxRallyCount))
    setText('coach-reaction-label', '近5局最高')
  } else {
    setText('coach-reaction-value', '--')
    setText('coach-reaction-label', '样本不足')
  }

  if (strongest) {
    setText('coach-strong-value', strongest.value)
    setText('coach-strong-label', strongest.label)
  } else {
    setText('coach-strong-value', '--')
    setText('coach-strong-label', '样本不足')
  }

  if (weakness) {
    setText('coach-weak-value', weakness.value)
    setText('coach-weak-label', weakness.label)
  } else {
    setText('coach-weak-value', '--')
    setText('coach-weak-label', '样本不足')
  }
}

function renderAbilityMetrics(
  view: 'current' | 'recent',
  summary: MatchSummary,
  performance: MatchPerformanceSnapshot | null,
  canShowCurrentScore: boolean,
  isLiveCurrent = false
): void {
  const hasSummary = view === 'current' ? hasPerformanceSamples(performance ?? EMPTY_PERFORMANCE) : Boolean(summary.matchId)
  const canShowScore = view === 'current' ? canShowCurrentScore : hasSummary
  const emptyMetricValue = isLiveCurrent ? '采样中' : '--'
  const matchScoreTitle = byId('match-score-title')
  if (matchScoreTitle) matchScoreTitle.textContent = '竞技指数'
  setText('ability-panel-title', view === 'current' ? '本局' : '上一局')
  setText('match-rating-value', canShowScore ? `${summary.rating}${summary.title} · ${summary.score}` : emptyMetricValue)
  setText('match-rating-label', '')
  const matchScoreCard = byId('match-score-card')
  matchScoreCard?.style.setProperty('--metric-fill', canShowScore ? `${Math.round((summary.score / 999) * 100)}%` : '0%')
  matchScoreCard?.classList.toggle('has-value', canShowScore)

  setMetricCard(
    'rally',
    hasSummary && summary.maxRallyCount > 0 ? String(summary.maxRallyCount) : emptyMetricValue,
    '',
    clamp((summary.maxRallyCount / 20) * 100, 0, 100)
  )

  const reactionMs = summary.threatReactionP50Ms
  const hasTrustedReaction = reactionMs !== null
  setMetricCard(
    'reaction',
    hasTrustedReaction ? `${reactionMs}ms` : emptyMetricValue,
    hasTrustedReaction && (summary.reactionSamples ?? 0) > 0 ? `样本 ${summary.reactionSamples} 次` : '',
    hasTrustedReaction ? reactionScore(reactionMs) : 0
  )
  setRateMetric(
    'preposition',
    summary.prePositionRate,
    summary.prePositionSuccesses,
    summary.prePositionAttempts,
    '站对',
    (summary.prePositionAttempts ?? 0) >= PREPOSITION_TRUSTED_SAMPLE_COUNT,
    emptyMetricValue
  )
  setRateMetric(
    'pressure',
    summary.highPressureReceiveRate,
    summary.highPressureReturns,
    summary.highPressureAttempts,
    '接回',
    (summary.highPressureAttempts ?? 0) >= HIGH_PRESSURE_TRUSTED_SAMPLE_COUNT,
    emptyMetricValue
  )
  setRateMetric(
    'defense',
    summary.receiveSuccessRate,
    summary.playerReceiveSuccesses,
    summary.playerReceiveAttempts,
    '接回',
    (summary.playerReceiveAttempts ?? 0) >= RECEIVE_TRUSTED_SAMPLE_COUNT,
    emptyMetricValue
  )

  const returnQuality = Math.round(clamp(summary.returnQuality ?? 0, 0, 100))
  const returnSamples = Math.max(0, Math.floor(summary.returnQualitySamples ?? 0))
  const hasTrustedReturn = returnSamples >= RETURN_QUALITY_TRUSTED_SAMPLE_COUNT
  setMetricCard(
    'return',
    hasTrustedReturn && returnQuality > 0 ? `${returnQuality}` : emptyMetricValue,
    hasTrustedReturn ? `均速 ${Math.round(summary.avgReturnSpeed ?? 0)}` : '',
    hasTrustedReturn ? returnQuality : 0
  )
  setRateMetric(
    'control',
    summary.sweetSpotRate,
    summary.playerCenterHits,
    summary.playerHits,
    '中心',
    (summary.playerHits ?? 0) >= CONTROL_TRUSTED_SAMPLE_COUNT,
    emptyMetricValue
  )

  const tempoScore = Math.round(clamp(summary.tempoPressure ?? 0, 0, 100))
  const tempoSamples = Math.max(0, Math.floor(summary.returnSpeedSamples ?? 0))
  setMetricCard(
    'tempo',
    tempoSamples >= TEMPO_TRUSTED_SAMPLE_COUNT ? `${tempoScore}` : emptyMetricValue,
    tempoSamples >= TEMPO_TRUSTED_SAMPLE_COUNT ? `均速 ${Math.round(summary.avgReturnSpeed ?? 0)} · 高速 ${summary.highSpeedReturnRate ?? 0}%` : '',
    tempoSamples >= TEMPO_TRUSTED_SAMPLE_COUNT ? tempoScore : 0
  )

  const spinSamples = Math.max(0, Math.floor(summary.spinSamples ?? 0))
  const spinConversions = Math.max(0, Math.floor(summary.spinConversions ?? 0)) + Math.max(0, Math.floor(summary.spinWeakReturns ?? 0))
  const hasTrustedSpin = spinSamples >= CONTROL_TRUSTED_SAMPLE_COUNT && summary.spinThreat > 0
  setMetricCard(
    'spin',
    hasTrustedSpin ? `${summary.spinThreat}` : emptyMetricValue,
    hasTrustedSpin
      ? spinConversions > 0
        ? `转化 ${spinConversions}/${spinSamples}`
        : `有效 ${spinSamples} · 未转化`
      : '',
    hasTrustedSpin ? summary.spinThreat : 0
  )

  const pressureAttempts = Math.max(0, Math.floor(summary.pressureConversionAttempts ?? 0))
  const pressureConversions = Math.max(0, Math.floor(summary.pressureConversionCount ?? 0))
  const pressureScore = Math.round(clamp(summary.pressureConversion ?? 0, 0, 100))
  setMetricCard(
    'offense',
    pressureAttempts > 0 ? `${pressureScore}%` : emptyMetricValue,
    pressureAttempts > 0 ? `${pressureConversions}/${pressureAttempts} 转化` : '',
    pressureAttempts > 0 ? pressureScore : 0
  )
  const keyPointAttempts = Math.max(0, Math.floor(summary.keyPointAttempts ?? 0))
  const keyPointWins = Math.max(0, Math.floor(summary.keyPointWins ?? 0))
  if (keyPointAttempts >= KEY_POINT_TRUSTED_SAMPLE_COUNT) {
    setRateMetric('keypoint', summary.keyPointWinRate, keyPointWins, keyPointAttempts, '拿下')
  } else {
    setMetricCard(
      'keypoint',
      emptyMetricValue,
      isLiveCurrent ? '' : keyPointAttempts > 0 ? `${keyPointWins}/${keyPointAttempts} 样本少` : '',
      0
    )
  }
  setMetricCard(
    'clutch',
    summary.clutchSaveMs === null ? emptyMetricValue : `${summary.clutchSaveMs}ms`,
    summary.clutchSaveMs === null ? '' : '最佳剩余',
    summary.clutchSaveMs === null ? 0 : clamp(((320 - summary.clutchSaveMs) / 220) * 100, 0, 100)
  )
  setMetricCard(
    'curve',
    summary.curveShotCount > 0 ? `${summary.curveShotCount}次` : emptyMetricValue,
    summary.curveShotCount > 0 ? '高质量甩球' : '',
    clamp((summary.curveShotCount / 6) * 100, 0, 100)
  )
}

function renderPerformancePanel(): void {
  const performance = state.performance ?? EMPTY_PERFORMANCE
  const coachState = getCoachState()
  const currentSummary = computeMatchScore(performance)
  const bestMatch = getBestMatchRecord()
  const hasBestMatch = bestMatch.score > 0
  const currentIndex = hasBestMatch ? bestMatch.score : 0
  const currentRating = getRating(currentIndex)
  const nextRating = getNextRating(currentIndex)
  const recentAverage = getRecentAverage()
  const wins = recentMatches.filter((match) => match.won).length
  const avgRally = recentMatches.length > 0
    ? Math.round(recentMatches.reduce((sum, match) => sum + match.maxRallyCount, 0) / recentMatches.length)
    : 0
  const hasRecentMatches = recentMatches.length > 0
  const hasCompetitiveIndex = currentIndex > 0
  const hasCurrentSamples = hasPerformanceSamples(performance)
  const canShowCurrentScore = hasEnoughLiveScoreSamples(performance)
  const currentStatKey = getMatchStatKey(currentSummary)
  const previousMatches = performance.isComplete
    ? recentMatches.filter((match) => match.matchId !== performance.matchId && getMatchStatKey(match) !== currentStatKey)
    : recentMatches
  const hasPreviousMatch = previousMatches.length > 0
  const recentCoachSummary = computeRecentCoachSummary(recentMatches)
  const canUseCurrentView = coachState === 'playing' || (coachState === 'finished' && hasCurrentSamples)
  const canUseRecentView = coachState !== 'playing' && hasPreviousMatch
  const performanceView = coachState === 'playing'
    ? 'current'
    : getResolvedPerformanceView(canUseCurrentView, canUseRecentView)
  const displaySummary = performanceView === 'current' ? currentSummary : previousMatches[0]
  const shouldShowAbilitySection = canUseCurrentView || canUseRecentView
  byId('competitive-index-card')?.classList.toggle('has-basic-performance', hasCurrentSamples || hasRecentMatches || hasCompetitiveIndex)
  document.querySelector('.ability-section')?.classList.toggle(
    'is-empty',
    !shouldShowAbilitySection
  )
  const useRecentCoachSummary = coachState !== 'playing' && recentCoachSummary !== null
  const coachSummary = useRecentCoachSummary
    ? recentCoachSummary as MatchSummary
    : currentSummary
  const coachHasSummary = useRecentCoachSummary ? hasRecentMatches : hasCurrentSamples
  const coachSummaryScope: 'current' | 'recent' = useRecentCoachSummary ? 'recent' : 'current'
  const advancedPanel = byId<HTMLDetailsElement>('advanced-metrics-panel')
  if (advancedPanel && coachState === 'playing') advancedPanel.open = true

  renderCoachShell(coachState)
  setText('competitive-index-score', hasCompetitiveIndex ? String(currentIndex) : '--')
  setText('competitive-index-rating', hasCompetitiveIndex ? `${currentRating.rating} ${currentRating.title}` : '暂无评级')
  setText('competitive-index-cert', hasCompetitiveIndex ? getCertificationLabel(bestMatch.certDifficulty) : '暂无难度')
  setText('competitive-index-trend', hasCompetitiveIndex ? getNextRatingProgressText(currentIndex) : '完成一局后生成指数')
  byId('competitive-index-progress')?.style.setProperty('width', hasCompetitiveIndex ? `${Math.round(getRatingProgress(currentIndex))}%` : '0%')
  setText('competitive-progress-start', hasCompetitiveIndex ? `${currentRating.rating} ${currentRating.min}` : '--')
  setText('competitive-progress-end', hasCompetitiveIndex
    ? nextRating ? `${nextRating.rating} ${nextRating.min}` : 'MAX 999'
    : '--'
  )
  renderCoachSummary(coachSummary, coachHasSummary, coachSummaryScope)
  syncPerformanceViewTabs(
    performanceView,
    canUseCurrentView,
    canUseRecentView
  )
  renderAbilityMetrics(
    performanceView,
    displaySummary ?? currentSummary,
    performanceView === 'current' ? performance : null,
    canShowCurrentScore,
    coachState === 'playing' && performanceView === 'current'
  )
  if (coachState === 'playing') {
    setText('ability-panel-title', '本局')
  } else if (coachState === 'finished') {
    setText('ability-panel-title', performanceView === 'current' ? '本局' : '上一局')
  }

  setText('recent-average-rally', String(avgRally))
  setText('recent-win-rate', recentMatches.length > 0 ? `${Math.round((wins / recentMatches.length) * 100)}%` : '--')
  setText('recent-trend', getRecentTrendLabel(recentMatches))
  setText('recent-average-score', recentMatches.length > 0 ? String(recentAverage) : '--')
  const bestPrePosition = getBestPrePositionRecord()
  const hasBestPrePosition = bestPrePosition.attempts >= PREPOSITION_TRUSTED_SAMPLE_COUNT
  setText('best-rating', hasBestMatch ? `${bestMatch.rating} ${bestMatch.title}` : '--')
  setText('best-rating-detail', hasBestMatch ? getCertificationLabel(bestMatch.certDifficulty) : '')
  setText('best-index', hasBestMatch ? String(bestMatch.score) : '--')
  setText('best-index-detail', hasBestMatch ? '历史最高' : '')
  setText('best-hell-rally', personalBest.hellMaxRally > 0 ? String(personalBest.hellMaxRally) : '--')
  setText('best-hell-rally-detail', personalBest.hellMaxRally > 0 ? '地狱难度' : '')
  setText(
    'best-threat-reaction',
    personalBest.bestThreatReactionP50Ms === null ? '--' : `${personalBest.bestThreatReactionP50Ms}ms`
  )
  setText('best-threat-reaction-detail', personalBest.bestThreatReactionP50Ms === null ? '' : '历史最快')
  setText('best-preposition', hasBestPrePosition ? `${bestPrePosition.rate}%` : '--')
  setText(
    'best-preposition-detail',
    hasBestPrePosition ? `${bestPrePosition.successes}/${bestPrePosition.attempts} 站对` : ''
  )
  setText('best-curve-shot', personalBest.bestCurveShotCount > 0 ? `${personalBest.bestCurveShotCount}次` : '--')
  setText('best-curve-shot-detail', personalBest.bestCurveShotCount > 0 ? '单局最多' : '')
}

function animateChildrenFromPrevious(container: HTMLElement, previousRects: Map<string, DOMRect>): void {
  Array.from(container.children).forEach((child) => {
    const element = child as HTMLElement
    const key = element.dataset.key ?? ''
    const before = previousRects.get(key)
    if (!before) {
      element.classList.add('is-entering')
      requestAnimationFrame(() => element.classList.remove('is-entering'))
      return
    }

    const after = element.getBoundingClientRect()
    const deltaY = before.top - after.top
    if (Math.abs(deltaY) < 1) return

    element.style.transition = 'none'
    element.style.transform = `translateY(${deltaY}px)`
    requestAnimationFrame(() => {
      element.style.transition = ''
      element.style.transform = ''
    })
  })
}

function createEntryMeta(entry: DisplayEntry): HTMLElement {
  const meta = document.createElement('span')
  meta.className = 'leaderboard-entry-meta'
  if (activeBoard === 'index') {
    const ratingLabel = `${entry.rating ?? getRating(entry.score).rating} ${entry.title ?? getRating(entry.score).title}`
    meta.textContent = entry.isLive ? `${ratingLabel} · 本局试算` : ratingLabel
  } else {
    meta.textContent = entry.rank === 1 ? '最强王者' : '最高连击'
  }
  return meta
}

function createPodiumRankBadge(rank: number): HTMLImageElement {
  const img = document.createElement('img')
  img.className = `podium-rank podium-rank-${rank}`
  img.src = `assets/ui/leaderboard/rank-${rank}.png?v=20260514c`
  img.alt = `第${rank}名`
  img.decoding = 'async'
  img.draggable = false
  return img
}

function renderPodium(displayEntries: DisplayEntry[]): void {
  const podium = byId('leaderboard-podium')
  if (!podium) return

  const previousRects = new Map(
    Array.from(podium.children).map((child) => [
      (child as HTMLElement).dataset.key ?? '',
      (child as HTMLElement).getBoundingClientRect(),
    ])
  )
  const fragment = document.createDocumentFragment()
  const topEntries = displayEntries.slice(0, 3)

  if (topEntries.length <= 0) {
    const empty = document.createElement('div')
    empty.className = 'podium-empty'
    empty.textContent = activeBoard === 'index' ? '完成一局后进入竞技指数榜' : '开局后连击会实时进入榜单'
    fragment.append(empty)
    podium.replaceChildren(fragment)
    return
  }

  topEntries.forEach((entry) => {
    const rank = entry.rank
    const card = document.createElement('div')
    card.className = 'podium-card'
    card.dataset.podiumRank = String(rank)
    card.dataset.key = entry.deviceId
    card.classList.toggle('is-current', entry.isCurrent)
    card.classList.toggle('is-live', entry.isLive)

    const crown = document.createElement('span')
    crown.className = 'podium-crown'

    const rankBadge = createPodiumRankBadge(rank)

    const name = document.createElement('span')
    name.className = 'podium-name'
    name.textContent = entry.name

    const score = document.createElement('strong')
    score.className = 'podium-score'
    score.textContent = String(entry.score)

    const info = document.createElement('span')
    info.className = 'podium-info'
    info.append(name, createEntryMeta(entry))

    card.append(crown, rankBadge, info, score)
    fragment.append(card)
  })

  podium.replaceChildren(fragment)
  animateChildrenFromPrevious(podium, previousRects)
}

function renderLeaderboard(): void {
  const list = byId('leaderboard-list')
  const empty = byId('leaderboard-empty')
  const contextEl = byId('leaderboard-context')
  const badgeEl = byId('leaderboard-panel-badge')
  if (!list || !empty || !contextEl || !badgeEl) return

  document.querySelectorAll<HTMLButtonElement>('[data-leaderboard-tab]').forEach((button) => {
    const isActive = button.dataset.leaderboardTab === activeBoard
    button.classList.toggle('is-active', isActive)
    button.setAttribute('aria-pressed', isActive ? 'true' : 'false')
  })

  badgeEl.textContent = activeBoard === 'index' ? 'INDEX TOP 50' : 'HELL TOP 50'

  const displayEntries = buildDisplayEntries(activeBoard)
  const topEntries = displayEntries.slice(0, LEADERBOARD_LIMIT)
  const listEntries = displayEntries.slice(3, LEADERBOARD_LIMIT)
  const hasLiveIndexEntry = activeBoard === 'index' && displayEntries.some((entry) => entry.isCurrent && entry.isLive)
  renderPodium(displayEntries)

  const previousRects = new Map(
    Array.from(list.children).map((child) => [
      (child as HTMLElement).dataset.key ?? '',
      (child as HTMLElement).getBoundingClientRect(),
    ])
  )
  const fragment = document.createDocumentFragment()

  listEntries.forEach((entry) => {
    const row = document.createElement('li')
    row.className = 'leaderboard-row'
    row.dataset.key = entry.deviceId
    row.dataset.rank = String(entry.rank)
    row.classList.toggle('is-current', entry.isCurrent)
    row.classList.toggle('is-live', entry.isLive)

    const rank = document.createElement('span')
    rank.className = 'leaderboard-rank'
    rank.textContent = String(entry.rank).padStart(2, '0')

    const textWrap = document.createElement('span')
    textWrap.className = 'leaderboard-row-info'

    const name = document.createElement('span')
    name.className = 'leaderboard-player'
    name.textContent = entry.name
    textWrap.append(name, createEntryMeta(entry))

    const score = document.createElement('span')
    score.className = 'leaderboard-score'
    score.textContent = String(entry.score)

    row.append(rank, textWrap, score)
    fragment.append(row)
  })

  list.replaceChildren(fragment)
  animateChildrenFromPrevious(list, previousRects)

  empty.textContent = topEntries.length > 0 ? '第 4-50 名等待挑战' : '还没有上榜记录'
  empty.hidden = listEntries.length > 0

  const boardAvailable = boards[activeBoard].available
  if (!boardAvailable) {
    contextEl.textContent = '排行榜服务未连接，当前仅做页面试算'
  } else if (activeBoard === 'index') {
    contextEl.textContent = hasLiveIndexEntry
      ? '本局竞技指数试算中，结算后写入历史最高榜'
      : '按历史最高单局竞技指数排序，同分看难度与连击'
  } else {
    contextEl.textContent = '按历史最高连击数排序，仅地狱难度纳入排序'
  }
}

function renderAll(): void {
  renderDifficulty()
  renderNameArea()
  renderPerformancePanel()
  renderLeaderboard()
}

function handleStateChange(nextState: Partial<PingPongUiState>): void {
  const previousMatchId = state.performance?.matchId
  const enteredGameOver = !previousGameOver && Boolean(nextState.gameOver)
  state = {
    ...state,
    ...nextState,
    score: {
      ...state.score,
      ...(nextState.score ?? {}),
    },
    performance: {
      ...(state.performance ?? EMPTY_PERFORMANCE),
      ...(nextState.performance ?? {}),
    },
  }

  if (enteredGameOver) {
    selectedPerformanceView = 'current'
  }

  if (previousMatchId && state.performance?.matchId !== previousMatchId) {
    lastLiveSubmittedScore = 0
    queuedLiveSubmitScore = 0
    // 上一局的 server-side session 已失效（无论是已 finalize 还是被丢弃），
    // 下一次 heartbeat/finish 时会自动重新调用 /api/match/start。
    if (currentSession && currentSession.clientMatchId !== state.performance?.matchId) {
      currentSession = null
    }
  }

  renderAll()

  if (state.difficulty === 'hell' && state.maxRallyCount > lastLiveSubmittedScore) {
    queueLiveScoreSubmit(state.maxRallyCount)
  }

  if (!previousGameOver && state.gameOver && state.performance?.isComplete) {
    saveCompletedMatch(state.performance)
    renderPerformancePanel()
    renderNameArea()
    renderLeaderboard()
    scheduleGameOverPanelFocus()
  }

  if (!previousGameOver && state.gameOver && state.difficulty === 'hell') {
    queueLiveScoreSubmit(state.maxRallyCount, true)
  }

  previousGameOver = state.gameOver
}

function bindDifficultyButtons(): void {
  document.querySelectorAll<HTMLButtonElement>('[data-difficulty-option]').forEach((button) => {
    button.addEventListener('click', () => {
      if (!state.canChangeDifficulty) return
      const level = button.dataset.difficultyOption as Difficulty | undefined
      if (!level) return

      window.dispatchEvent(new CustomEvent('ping-pong:set-difficulty', {
        detail: { level },
      }))
    })
  })
}

function bindLeaderboardTabs(): void {
  document.querySelectorAll<HTMLButtonElement>('[data-leaderboard-tab]').forEach((button) => {
    button.addEventListener('click', () => {
      const board = button.dataset.leaderboardTab as LeaderboardBoard | undefined
      if (board !== 'index' && board !== 'rally') return
      activeBoard = board
      renderLeaderboard()
      void refreshLeaderboard(board).then(renderLeaderboard)
    })
  })
}

function bindPerformanceViewTabs(): void {
  document.querySelectorAll<HTMLButtonElement>('[data-performance-view]').forEach((button) => {
    button.addEventListener('click', () => {
      if (button.disabled || button.hidden) return
      const view = button.dataset.performanceView as 'current' | 'recent' | undefined
      if (view !== 'current' && view !== 'recent') return
      selectedPerformanceView = view
      renderPerformancePanel()
    })
  })
}

function bindNameForm(): void {
  const form = byId<HTMLFormElement>('leaderboard-name-form')
  const input = byId<HTMLInputElement>('leaderboard-name-input')
  const changeButton = byId<HTMLButtonElement>('leaderboard-change-name')
  if (!form || !input || !changeButton) return

  input.addEventListener('input', () => {
    input.value = Array.from(input.value).slice(0, MAX_NAME_LENGTH).join('')
  })

  form.addEventListener('submit', (event) => {
    event.preventDefault()
    const nextName = truncateName(input.value)
    if (!nextName) {
      input.focus()
      return
    }

    playerName = nextName
    editingName = false
    try {
      localStorage.setItem(PLAYER_NAME_KEY, playerName)
    } catch {
      // Local storage can be blocked in private contexts; the session name still works.
    }

    const scoreToSubmit = Math.max(pendingSubmitScore ?? 0, queuedLiveSubmitScore)
    const finishToSubmit = pendingMatchFinish
    renderAll()
    if (scoreToSubmit) queueLiveScoreSubmit(scoreToSubmit, true)
    if (finishToSubmit) void submitMatchFinish(finishToSubmit)
  })

  changeButton.addEventListener('click', () => {
    editingName = true
    renderNameArea()
    input.focus()
    input.select()
  })
}

export function initPingPongSidePanel(): void {
  if (initialized) return
  initialized = true
  deviceId = getDeviceId()
  deviceSecret = readStoredDeviceSecret()

  DIFFICULTIES.forEach(({ level, label, hint }) => {
    const button = document.querySelector<HTMLButtonElement>(`[data-difficulty-option="${level}"]`)
    if (!button) return
    button.querySelector('.difficulty-label')!.textContent = label
    button.querySelector('.difficulty-hint')!.textContent = hint
  })

  bindDifficultyButtons()
  bindLeaderboardTabs()
  bindPerformanceViewTabs()
  bindNameForm()
  window.addEventListener('ping-pong:state', (event) => {
    handleStateChange((event as CustomEvent<Partial<PingPongUiState>>).detail ?? {})
  })
  window.addEventListener('ping-pong:difficulty-changed', (event) => {
    const detail = (event as CustomEvent<{ level?: Difficulty; label?: string }>).detail
    if (!detail?.level) return
    handleStateChange({
      difficulty: detail.level,
      difficultyLabel: detail.label ?? state.difficultyLabel,
    })
  })

  renderAll()
  if (!USE_STATIC_MOBILE_LEADERBOARDS) {
    // 后台静默注册 deviceSecret，并刷新一次榜单。
    void ensureDeviceCredentials()
    void refreshLeaderboards()
    window.setInterval(() => {
      void refreshLeaderboards()
    }, 15_000)
  }
}
